import * as B from '@blueprintjs/core';
import classNames from 'classnames';
import * as I from 'immutable';
import React from 'react';

import {PushNotificationFn} from 'app/components/Notification';
import {
  ApiOrganization,
  ApiOrganizationUser,
  getOrgDefaultReportText,
} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {USER_ROLE_READONLY} from 'app/utils/constants';

import cs from './OrganizationView.styl';
import imageCs from '../ImageFileUpload.styl';

const DEFAULT_TEXT_AREA_ROWS = 8;

export const OrganizationDefaultReportText: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
    profile: I.ImmutableOf<ApiOrganizationUser>;
    setDefaultReportText: LoggedInUserActions['setOrganizationDefaultReportText'];
    pushNotification: PushNotificationFn;
  }>
> = ({organization, profile, setDefaultReportText: saveDefaultReportText, pushNotification}) => {
  const [defaultReportText, setDefaultReportText] = React.useState<string>(
    getOrgDefaultReportText(organization)
  );

  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await saveDefaultReportText(organization.get('id'), defaultReportText);
      pushNotification({
        message: 'Saved default report text.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.SUCCESS,
        },
      });
    } catch (e) {
      console.error(e);
      pushNotification({
        message: 'Error saving report text. Please try again.',
        autoHideDuration: 3000,
        options: {
          intent: B.Intent.DANGER,
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  const canUpdate = profile.get('role') !== USER_ROLE_READONLY;

  return (
    <>
      <div className={cs.field}>
        <label>Default report text</label>
        <p>
          This text appears on the first page of all future reports and serves as a shared template
          or prompt for your organization.
        </p>
        <div className={classNames(imageCs.file, cs.reportTextarea)}>
          <B.TextArea
            value={defaultReportText}
            onChange={(e) => setDefaultReportText(e.currentTarget.value)}
            fill={false}
            rows={DEFAULT_TEXT_AREA_ROWS}
            disabled={!canUpdate}
          />
          <B.Tooltip
            content={'Only organization admins can update this setting.'}
            disabled={canUpdate}
          >
            <B.Button
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
              intent={B.Intent.PRIMARY}
              small
            >
              Save
            </B.Button>
          </B.Tooltip>
        </div>
      </div>
    </>
  );
};
